import { store } from '@/stores'
import { defineStore } from 'pinia'
import { getAccessToken, removeToken } from '@/utils/auth'
import { CACHE_KEY, useCache, deleteUserCache } from '@/hooks/common/useCache'
import { LoginApi } from '@/api/login'
import { UserApi } from '@/api/user'
import type { IAppMemberFeatureRespVO } from '@/api/member'

const { wsCache } = useCache()

interface UserLevelVo {
  id: number
  name: string
  level: number
  icon: string
}
interface UserVO {
  id: number
  createTime: number
  avatar: string
  nickname: string
  mobile: string
  deptId: number
  level: UserLevelVo
}

interface UserInfoVO {
  // USER 缓存
  permissions: Set<string>
  roles: string[]
  isSetUser: boolean
  user: UserVO
  memberFeatureConf: IAppMemberFeatureRespVO
}

export const useUserStore = defineStore('admin-user', {
  state: (): UserInfoVO => ({
    permissions: new Set<string>(),
    roles: [],
    isSetUser: false,
    user: {
      id: 0,
      avatar: '',
      nickname: '',
      deptId: 0,
      mobile: '',
      createTime: 0,
      level: {
        id: 0,
        name: '',
        level: 0,
        icon: '',
      },
    },
    memberFeatureConf: {
      memberId: 0,
      roleName: '免费会员',
      spaceMax: 0,
      spaceUsed: 0,
      panoMax: 0,
      panoUsed: 0,
      viewMax: 0,
      viewUsed: 0,
      tempLinkMax: 0,
      tempLinkUsed: 0,
    },
  }),
  getters: {
    getMemberFeatureSpaceIsExceed(): boolean {
      return this.memberFeatureConf.spaceUsed >= this.memberFeatureConf.spaceMax
    },
    getMemberFeaturePanoIsExceed(): boolean {
      return this.memberFeatureConf.panoUsed >= this.memberFeatureConf.panoMax
    },
    getMemberFeatureConf(): IAppMemberFeatureRespVO {
      return this.memberFeatureConf
    },
    getPermissions(): Set<string> {
      return this.permissions
    },
    getRoles(): string[] {
      return this.roles
    },
    getIsSetUser(): boolean {
      return this.isSetUser
    },
    getUser(): UserVO {
      return this.user
    },
  },
  actions: {
    setMemberFeatureConf(conf: IAppMemberFeatureRespVO) {
      this.memberFeatureConf = conf
    },
    async setUserInfoAction() {
      if (!getAccessToken()) {
        this.resetState()
        return null
      }
      let userInfo = wsCache.get(CACHE_KEY.USER)
      if (!userInfo) {
        userInfo = await UserApi.getUserInfo()
      } else {
        // 特殊：在有缓存的情况下，进行加载。但是即使加载失败，也不影响后续的操作，保证可以进入系统
        try {
          userInfo = await UserApi.getUserInfo()
        } catch (error) {
          console.log(error)
        }
      }
      // this.permissions = new Set(userInfo.permissions || []) // 兜底为 [] https://t.zsxq.com/xCJew
      // this.roles = userInfo.roles
      this.user = userInfo
      this.isSetUser = true
      wsCache.set(CACHE_KEY.USER, userInfo)
      // wsCache.set(CACHE_KEY.ROLE_ROUTERS, userInfo.menus)
    },
    async setUserAvatarAction(avatar: string) {
      const userInfo = wsCache.get(CACHE_KEY.USER)
      // NOTE: 是否需要像`setUserInfoAction`一样判断`userInfo != null`
      this.user.avatar = avatar
      userInfo.user.avatar = avatar
      wsCache.set(CACHE_KEY.USER, userInfo)
    },
    async setUserNicknameAction(nickname: string) {
      const userInfo = wsCache.get(CACHE_KEY.USER)
      // NOTE: 是否需要像`setUserInfoAction`一样判断`userInfo != null`
      this.user.nickname = nickname
      userInfo.user.nickname = nickname
      wsCache.set(CACHE_KEY.USER, userInfo)
    },
    async loginOut() {
      await LoginApi.authLogout()
      removeToken()
      deleteUserCache() // 删除用户缓存
      this.resetState()
    },
    resetState() {
      this.permissions = new Set<string>()
      this.roles = []
      this.isSetUser = false
      this.user = {
        id: 0,
        avatar: '',
        nickname: '',
        mobile: '',
        deptId: 0,
        createTime: 0,
        level: {
          id: 0,
          name: '',
          level: 0,
          icon: '',
        },
      }
    },
  },
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
